/**
 * BriefCanvas — composite of 8 BriefSectionCards.
 *
 * Used by both the brief-share SPA's right pane and the admin
 * BriefWorkspace's DocumentLens.
 */

import type { BriefSnapshot, BriefDraftResponse } from '../types';
import { BriefSectionCard, SECTION_ORDER, type SectionKey } from './BriefSectionCard';

export type { SectionKey };
export { SECTION_ORDER };

export type BriefDraftSections = Partial<BriefDraftResponse['sections']>;

interface BriefCanvasProps {
  brief: BriefSnapshot;
  activeSections: string[];
  recentlyPatched: Set<string>;
  onTogglePin: (section: string) => void;
  canPin: boolean;
  activeDraft: BriefDraftSections | null;
  onDraftSection: (section: SectionKey) => void;
  onQuickApply: (section: SectionKey, value: string) => Promise<void>;
  canInteract: boolean;
  /** When true, omit the brief-name header (host page renders its own). */
  headerless?: boolean;
  className?: string;
}

export function BriefCanvas({
  brief,
  activeSections,
  recentlyPatched,
  onTogglePin,
  canPin,
  activeDraft,
  onDraftSection,
  onQuickApply,
  canInteract,
  headerless = false,
  className,
}: BriefCanvasProps) {
  return (
    <main className={className ?? 'flex-1 min-w-0 h-full overflow-y-auto bg-slate-50'}>
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {!headerless && (
          <header>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                Brief · v{brief.version}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                · {brief.status}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">{brief.name}</h1>
            <p className="text-sm text-slate-500">Client · {brief.client_uid}</p>
          </header>
        )}

        <div className="grid gap-4">
          {SECTION_ORDER.map(({ key, label, tint }) => {
            const draftValue = mapDraftValue(key, activeDraft);
            return (
              <BriefSectionCard
                key={key}
                sectionKey={key}
                label={label}
                tint={tint}
                data={(brief[key] as Record<string, unknown>) || null}
                isPinned={activeSections.includes(key)}
                wasPatched={recentlyPatched.has(key)}
                onTogglePin={canPin ? () => onTogglePin(key) : undefined}
                draftValue={draftValue}
                onDraftSection={() => onDraftSection(key)}
                onQuickApply={
                  draftValue && canInteract
                    ? () => onQuickApply(key, draftValue)
                    : undefined
                }
                canInteract={canInteract}
              />
            );
          })}
        </div>
      </div>
    </main>
  );
}

function mapDraftValue(
  key: SectionKey,
  draft: BriefDraftSections | null,
): string | undefined {
  if (!draft) return undefined;
  if (key === 'objective') return draft.objective || undefined;
  if (key === 'audience') return draft.audience_snapshot || undefined;
  if (key === 'insight') return draft.insight || undefined;
  return undefined;
}
